import { Box, Popover, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import LoadingIcon from "../../../components/LoadingIcon";
import { Toast } from "../../../components/Toast";
import { useFileNaming } from "../../../hooks/useFileNaming";
import { LATEST } from "../../../utils/constants";
import NISTFunction from "./components/NISTFunction";
import NistOverviewHeader from "./components/NistOverviewHeader";

export default function NistView() {
  const colors = useColors();
  const nistViewRef = useRef();
  const { trackEvent, getNISTSnapshot } = useAPI();
  const [selectedNistSnapshot, setSelectedNistSnapshot] = useState({});
  const [timePoint, setTimePoint] = useState(LATEST);
  const [anchorEl, setAnchorEl] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({ searchString: "" });
  const NIST_POPOVER_WIDTH = "691px";

  const handlePopoverClose = () => {
    trackEvent("User closed NIST score calculation popover");
    setAnchorEl(null);
  };
  const { generateCsvFileName } = useFileNaming();
  const { data: nistSnapshot, isLoading: isLoadingNistSnapshot } = useQuery({
    queryKey: ["nistSnapshot"],
    queryFn: getNISTSnapshot,
    select: (data) => data.data,
  });

  const handleExportCSV = () => {
    trackEvent("User click on 'Export' button to download the NIST CSF table");
    try {
      // Create CSV header with simplified columns
      let csvContent = '"Function","Category","Score","Controls"';
      csvContent += "\n";

      // Add data rows
      Object.values(selectedNistSnapshot).forEach((func) => {
        func.categories.forEach((category) => {
          // Get simplified category name (without the ID part)
          const fullName = category.name;
          // Extract the name part without the ID
          const simpleName = fullName.includes(" (")
            ? fullName.substring(0, fullName.lastIndexOf(" ("))
            : fullName;

          // Always wrap category name in quotes to prevent CSV splitting
          const escapedName = `"${simpleName}"`;

          // Format controls as a comma-separated list in a single column
          const controlsList =
            category.controls && category.controls.length > 0
              ? `"${category.controls.sort((a, b) => a - b).join(", ")}"`
              : `""`;

          // Add all data to the row
          csvContent += `"${func.function}",${escapedName},${
            category.score >= 0 ? category.score : "N/A"
          },${controlsList}`;

          csvContent += "\n";
        });
      });

      // Create a blob and trigger download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", generateCsvFileName("nist_csf"));
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      Toast.error("Failed to export NIST CSF, Please try again later");
      trackEvent("Failed to export NIST CSF", { error: error.message });
    }
  };

  const handleTimePointClick = (label, option) => {
    setTimePoint(label);
    setSelectedNistSnapshot(option);
    trackEvent(`User changed time range to ${label} on NIST View`);
  };

  const initialSnapshot = useMemo(() => {
    return nistSnapshot?.[LATEST] || {};
  }, [nistSnapshot]);

  useEffect(() => {
    if (initialSnapshot && Object.keys(initialSnapshot).length > 0) {
      setSelectedNistSnapshot(initialSnapshot);
    }
  }, [initialSnapshot]);

  const overallScore = useMemo(() => {
    if (
      !selectedNistSnapshot ||
      Object.keys(selectedNistSnapshot).length === 0
    ) {
      return 0;
    }

    const allCategories = Object.values(selectedNistSnapshot).flatMap(
      (func) => func.categories
    );
    const scoredCategories = allCategories.filter((cat) => cat.score >= 0);

    if (scoredCategories.length === 0) {
      return 0;
    }

    const sum = scoredCategories.reduce((acc, cat) => acc + cat.score, 0);
    return Math.round(sum / scoredCategories.length);
  }, [selectedNistSnapshot]);

  const filteredSnapshot = useMemo(() => {
    const searchString =
      appliedFilters.searchString?.toLowerCase().trim() || "";

    if (!searchString || !selectedNistSnapshot) {
      return selectedNistSnapshot;
    }

    const filtered = {};

    Object.values(selectedNistSnapshot).forEach((nistFunction) => {
      const functionNameMatches = nistFunction.function
        .toLowerCase()
        .includes(searchString);

      if (functionNameMatches) {
        filtered[nistFunction.function] = nistFunction;
      } else {
        const matchingCategories = nistFunction.categories.filter(
          (category) =>
            category.name.toLowerCase().includes(searchString) ||
            category.id.toLowerCase().includes(searchString)
        );

        if (matchingCategories.length > 0) {
          filtered[nistFunction.function] = {
            ...nistFunction,
            categories: matchingCategories,
          };
        }
      }
    });

    return filtered;
  }, [selectedNistSnapshot, appliedFilters.searchString]);

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <NistOverviewHeader
        overallScore={overallScore}
        appliedFilters={appliedFilters}
        setAppliedFilters={setAppliedFilters}
        nistSnapshot={nistSnapshot}
        isLoadingNistSnapshot={isLoadingNistSnapshot}
        timePoint={timePoint}
        handleTimePointClick={handleTimePointClick}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        handleExportCSV={handleExportCSV}
        selectedNistSnapshot={selectedNistSnapshot}
        nistViewRef={nistViewRef}
      />

      <Popover
        id="nist-score-calculation-popover"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            p: 4,
            maxWidth: NIST_POPOVER_WIDTH,
            width: NIST_POPOVER_WIDTH,
            borderRadius: "10px",
            boxShadow: "1px 1px 8.5px 3px rgba(203, 203, 203, 0.50)",
          },
        }}
        disableRestoreFocus
      >
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            NIST Score Calculation
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            The coverage score for each NIST category is calculated as a
            weighted average of the controls mapped to it.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Each control contributes proportionally to the total number of
            controls in the category. Control scores are based on implementation
            status, entity coverage, and the severity of the associated gap —
            higher severity gaps have greater impact on the score.
            Higher-performing controls lead to higher category scores.
          </Typography>

          <Typography variant="subtitle2">
            Function-level scores are then calculated as the average of their
            category scores.
          </Typography>
        </Box>
      </Popover>

      {isLoadingNistSnapshot ? (
        <LoadingIcon size={16} />
      ) : selectedNistSnapshot && Object.keys(filteredSnapshot).length > 0 ? (
        <Box
          ref={nistViewRef}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          {Object.values(filteredSnapshot).map((nistFunction) => {
            return (
              <NISTFunction
                key={nistFunction.function}
                nistFunction={nistFunction}
              />
            );
          })}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            minHeight: "200px",
          }}
        >
          <Typography variant="subtitle1" sx={{ color: colors.text_secondary }}>
            No data available
          </Typography>
        </Box>
      )}
    </Box>
  );

  return content;
}
